import {
  Box,
  Checkbox,
  HStack,
  IconButton,
  Image,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useState } from "react";
import {
  RiDeleteBin4Line,
  RiDownloadLine,
  RiSendPlaneLine,
  RiStarLine,
} from "@remixicon/react";
import { Content, LikeOrCollect } from "@/types/content";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectCurrentImage, setCurrentImage } from "@/reducer/designslice";
import Publish from "@/components/publish";
import TransitionDialog from "@/components/alertDialog";
import Client4 from "@/services/api/client4";
import { contentRemove } from "@/reducer/contentSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/reducer/userslice";
import { ServerError } from "@/types/errors";
import { useCollectContentMutation } from "@/services/api/endpoints/content";

type Props = {
  content: Content;
  index: number;
  isBatch: boolean;
  onClose: () => void;
  handleCheckBox: (e: React.ChangeEvent<HTMLInputElement>, id: string) => void;
};

const HistoryModalItem = (props: Props) => {
  const dispatch = useAppDispatch();
  const { content, index, isBatch, handleCheckBox } = props;
  const [hovered, setHovered] = useState(false);
  const [currentContentId, setCurrentContentId] = useState("");

  const publishControl = useDisclosure();
  const dialogControl = useDisclosure();

  const user = useSelector(selectUser);
  const userId = user.id;
  const toast = useToast();
  const [collectContent] = useCollectContentMutation();
  const currentImage = useAppSelector(selectCurrentImage);

  const onDownload = useCallback(async () => {
    if (!content) {
      return;
    }
    try {
      const response = await fetch(content.image_url as string);
      const blob = await response.blob();
      const link = document.createElement("a") as HTMLAnchorElement;
      link.href = URL.createObjectURL(blob);
      link.download = generateRandomCode(9) + ".png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error fetching the image", error);
    }
  }, [content]);

  const generateRandomCode = (length: number): string => {
    let code = "";
    for (let j = 0; j < length; j++) {
      code += Math.floor(Math.random() * 10);
    }
    return code;
  };

  const onPublish = (item: Content) => {
    setCurrentContentId(item.id);
    publishControl.onOpen();
  };

  const onDelete = (item: Content) => {
    setCurrentContentId(item.id);
    dialogControl.onOpen();
  };

  const handleOnDelete = () => {
    dispatch(contentRemove(currentContentId));
    Client4.deleteContent(currentContentId);
    if (currentImage.id === currentContentId) {
      dispatch(setCurrentImage({} as Content));
    }
    dialogControl.onClose();
  };

  const onCollect = async (item: Content) => {
    const collectData: LikeOrCollect = {
      content_id: item.id,
      user_id: userId,
      status: !item.is_collect,
    };
    await collectContent(collectData)
      .unwrap()
      .catch((e) => {
        toast({
          title: (e as ServerError).message,
          position: "top",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
      });
  };

  return (
    <Box
      position="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Box
        sx={{
          position: "absolute",
          top: "5px",
          left: "5px",
          bgColor: "#000000",
          opacity: 0.5,
          fontSize: "12px",
          color: "white",
          textAlign: "center",
          w: "24px",
          h: "16px",
          lineHeight: "16px",
        }}
      >
        {index + 1}
      </Box>
      {isBatch && (
        <Checkbox
          position="absolute"
          top="5px"
          right="5px"
          isChecked={content.isChecked}
          onChange={(e) => handleCheckBox(e, content.id)}
        />
      )}
      <HStack
        sx={{
          background:
            "linear-gradient(0deg, rgba(39, 49, 77, 0.7) 0%, rgba(39, 49, 77, 0) 100%)",
          position: "absolute",
          h: "24px",
          w: "100%",
          bottom: 0,
        }}
        spacing="0"
        opacity={hovered ? 1 : 0}
        justify="center"
      >
        <Tooltip label="下载" placement="top">
          <IconButton
            aria-label="download img"
            size="xs"
            variant="unstyle"
            color="white"
            icon={<RiDownloadLine size={16} />}
            _hover={{ color: "#8E94A9" }}
            onClick={onDownload}
          />
        </Tooltip>
        <Tooltip label="发布" placement="top">
          <IconButton
            aria-label="publish img"
            size="xs"
            variant="unstyle"
            color="white"
            icon={<RiSendPlaneLine size={16} />}
            _hover={{ color: "#8E94A9" }}
            onClick={() => onPublish(content)}
          />
        </Tooltip>
        <Tooltip label="删除" placement="top">
          <IconButton
            aria-label="delete img"
            size="xs"
            variant="unstyle"
            color="white"
            icon={<RiDeleteBin4Line size={16} />}
            _hover={{ color: "#8E94A9" }}
            onClick={() => onDelete(content)}
          />
        </Tooltip>
        <Tooltip label="收藏" placement="top">
          <IconButton
            aria-label="collect img"
            size="xs"
            variant="unstyle"
            color={content.is_collect ? "red" : "white"}
            icon={<RiStarLine size={16} />}
            _hover={{ color: "#8E94A9" }}
            onClick={() => onCollect(content)}
          />
        </Tooltip>
      </HStack>
      <Image src={content.image_url} w="100%" h="150px" fit="cover" />

      {currentContentId && (
        <Publish
          isOpen={publishControl.isOpen}
          onClose={publishControl.onClose}
          contentId={currentContentId}
        />
      )}

      {currentContentId && (
        <TransitionDialog
          isOpen={dialogControl.isOpen}
          onClose={dialogControl.onClose}
          onDelete={handleOnDelete}
        />
      )}
    </Box>
  );
};

export default HistoryModalItem;
